import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'القسم ١: مفاهيم وتعريفات القياس',
  'القسم ٢: إجراءات جمع البيانات',
  'القسم ٣: نزاهة البيانات واتفاقية بين المراقبين',
  'القسم ٤: عرض البيانات وتمثيلها بيانيًا',
  'القسم ٥: تحليل البيانات وتفسيرها',
  'القسم ٦: التقييم المتقدم (مستوى تحليل الجودة السلوكي)'
];

const AttendanceDialog = ({ student, onClose, onUpdate }) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0]);
  const [attendance, setAttendance] = useState({});
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (student) {
      fetchAttendance();
    }
  }, [student]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/teacher/attendance/${student.email}`);
      
      // Build attendance map
      const attMap = {};
      response.data.records.forEach(record => {
        const key = `${record.category}_${record.day_number}`;
        attMap[key] = record.status;
      });
      
      setAttendance(attMap);
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAttendance = async (category, day, status) => {
    try {
      await axios.post(`${API}/teacher/attendance`, {
        student_email: student.email,
        category,
        day_number: day,
        status
      });
      
      // Update local state
      const key = `${category}_${day}`;
      setAttendance({ ...attendance, [key]: status });
      
      // Refresh statistics
      fetchAttendance();
      onUpdate();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const getAttendanceStatus = (category, day) => {
    const key = `${category}_${day}`;
    return attendance[key] || null;
  };

  return (
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{t('attendanceTracking') || 'Attendance Tracking'} - {student?.name}</DialogTitle>
      </DialogHeader>
      
      <div className="space-y-4">
        {/* Category Selector */}
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Statistics for selected category */}
        {statistics[selectedCategory] && (
          <Card className="bg-blue-50">
            <CardContent className="pt-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-600">{t('present') || 'Present'}</p>
                  <p className="text-2xl font-bold text-green-600">{statistics[selectedCategory].present}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('absent') || 'Absent'}</p>
                  <p className="text-2xl font-bold text-red-600">{statistics[selectedCategory].absent}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('attendanceRate') || 'Attendance %'}</p>
                  <p className="text-2xl font-bold text-blue-600">{statistics[selectedCategory].percentage}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Attendance Grid (24 days) */}
        <div className="grid grid-cols-6 gap-2">
          {Array.from({ length: 24 }, (_, i) => i + 1).map(day => {
            const status = getAttendanceStatus(selectedCategory, day);
            return (
              <Card key={day} className={`p-3 cursor-pointer ${
                status === 'present' ? 'bg-green-50 border-green-300' :
                status === 'absent' ? 'bg-red-50 border-red-300' : 'bg-gray-50'
              }`}>
                <div className="text-center">
                  <p className="text-xs font-semibold mb-2">{t('day')} {day}</p>
                  <div className="flex gap-1 justify-center">
                    <Button
                      size="sm"
                      variant={status === 'present' ? 'default' : 'outline'}
                      onClick={() => markAttendance(selectedCategory, day, 'present')}
                      className="h-8 w-8 p-0"
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant={status === 'absent' ? 'destructive' : 'outline'}
                      onClick={() => markAttendance(selectedCategory, day, 'absent')}
                      className="h-8 w-8 p-0"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </DialogContent>
  );
};

export default AttendanceDialog;
